/**
 * Garde un élément à la même hauteur d'écran pendant que le contenu change autour de lui.
 *
 * Les listes groupées par phase sont bien plus hautes que leur version à plat, et leurs
 * cartes sont en bas de page. En bas de page, repasser en liste raccourcissait le
 * document sous la position de défilement : le navigateur remontait d'un coup au
 * nouveau bas de page et le bouton filait sous le doigt. Aucune règle CSS ne couvre ce
 * cas (et Safari n'a pas d'ancrage de défilement).
 *
 * Après le changement, on remet l'élément là où il était. S'il faut pour cela plus de
 * hauteur que la page n'en a, on la complète par un espace vide en fin de document,
 * qui fond à mesure que l'on remonte.
 *
 * Le maintien dure `HOLD_MS` — de quoi couvrir un rechargement réseau (période des
 * symptômes de la page Cycle) — et cesse au premier geste de l'utilisateur.
 */
const HOLD_MS = 1500;
// Gestes qui rendent la main à l'utilisateur : on ne lutte pas contre son défilement.
const USER_INPUT = ['wheel', 'pointerdown', 'keydown', 'touchmove'];

let spacer = null;
let release = null;

function spacerHeight() {
  return spacer ? parseFloat(spacer.style.height) || 0 : 0;
}

// L'espace ne sert qu'à ne pas être renvoyé en bas de page : dès que l'on remonte, on
// le réduit à ce qui reste nécessaire pour que le bas de l'écran ne tombe pas dans le
// vide. Comme il est sous l'écran, le réduire ne déplace rien de visible.
function shrinkSpacer() {
  if (!spacer) return;
  const needed = window.scrollY + window.innerHeight - spacer.offsetTop;
  if (needed < spacerHeight()) setSpacerHeight(needed);
}

function setSpacerHeight(height) {
  const h = Math.max(0, Math.ceil(height));
  if (h > 0 && (!spacer || !spacer.isConnected)) {
    spacer = document.createElement('div');
    spacer.setAttribute('aria-hidden', 'true');
    spacer.dataset.scrollSpacer = '';
    document.body.appendChild(spacer);
  }
  if (!spacer) return;
  spacer.style.height = `${h}px`;
  if (h > 0) window.addEventListener('scroll', shrinkSpacer, { passive: true });
  else window.removeEventListener('scroll', shrinkSpacer);
}

/** Abandonne le maintien en cours et retire l'espace ajouté (changement de page). */
export function resetScrollHold() {
  release?.();
  setSpacerHeight(0);
}

/** Maintient `el` à sa position actuelle à l'écran malgré les changements qui suivent. */
export function holdScrollPosition(el) {
  if (!el || typeof window === 'undefined') return;
  release?.();

  const top = el.getBoundingClientRect().top;

  const fix = () => {
    if (!el.isConnected) return;
    const drift = el.getBoundingClientRect().top - top;
    if (Math.abs(drift) < 1) return;
    const target = Math.max(0, window.scrollY + drift);
    const deficit = target + window.innerHeight - document.documentElement.scrollHeight;
    if (deficit > 0) setSpacerHeight(spacerHeight() + deficit);
    window.scrollTo({ top: target, behavior: 'instant' });
  };

  // Le recul en bas de page se produit quand le contenu raccourcit : on surveille la
  // racine de l'app (et non `body`, qui contient l'espace ajouté) — le rappel tombe
  // après la mise en page et avant l'affichage, rien ne saute à l'écran.
  const root = document.getElementById('root') ?? document.body;
  const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fix) : null;
  observer?.observe(root);
  const frame = requestAnimationFrame(fix);

  const stop = () => {
    observer?.disconnect();
    cancelAnimationFrame(frame);
    clearTimeout(timer);
    USER_INPUT.forEach((type) => window.removeEventListener(type, stop, true));
    if (release === stop) release = null;
  };
  const timer = setTimeout(stop, HOLD_MS);
  USER_INPUT.forEach((type) => window.addEventListener(type, stop, { capture: true, passive: true }));
  release = stop;
}
